import { useEffect, useLayoutEffect, useState } from 'react';

const MORE_BUTTON_WIDTH = 80;

const useIsomorphicLayoutEffect =
    typeof window === 'undefined' ? useEffect : useLayoutEffect;

const fitCount = (widths, available, total, buttonWidth, gap) => {
    for (let count = widths.length; count > 1; count--) {
        const row =
            widths.slice(0, count).reduce((sum, width) => sum + width, 0) +
            gap * (count - 1);
        const button = count < total ? gap + buttonWidth : 0;
        if (row + button <= available) return count;
    }
    return 1;
};

// How many of the first `max` chips fit on one line of `rowRef`, leaving room
// for the "+N more" button whenever some are left out. `key` should change
// whenever the chips do, even if their count stays the same.
const useFitCount = ({
    rowRef,
    buttonRef,
    measure,
    total,
    max,
    key,
    gap = 0,
    enabled = true,
}) => {
    const [visible, setVisible] = useState(Math.min(max, total));
    const hasButton = visible < total;

    useIsomorphicLayoutEffect(() => {
        if (!enabled || !rowRef.current) return undefined;
        let disposed = false;
        const update = () => {
            if (disposed || !rowRef.current) return;
            setVisible(
                fitCount(
                    measure().slice(0, max),
                    rowRef.current.clientWidth,
                    total,
                    buttonRef.current?.offsetWidth ?? MORE_BUTTON_WIDTH,
                    gap
                )
            );
        };
        update();
        // Re-rendering inside the callback resizes the observed nodes in the
        // same frame, which browsers report as a "ResizeObserver loop" error.
        let frame = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        });
        observer.observe(rowRef.current);
        // Re-measure once the button renders and whenever its label resizes;
        // whether it shows never depends on its width, so this cannot loop.
        if (buttonRef.current) observer.observe(buttonRef.current);
        document.fonts?.ready.then(update);
        return () => {
            disposed = true;
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [enabled, total, key, hasButton]);

    return visible;
};

export default useFitCount;
